// @flow
import * as React from 'react';
import type { Match } from 'react-router-dom';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { withRouter } from 'react-router';
import { injectIntl, IntlShape } from 'react-intl';
import { Box } from 'grommet';
import { isEqual } from 'lodash';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import { numberPointsCallbacksFiber } from 'esm-web-pro/containers/pages/network/objects/fiber/MapFiber';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import point14Messages from 'esm-web-pro/messages/points14Messages';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import useHighlightFiber from 'esm-web-pro/containers/pages/network/objects/fiber/useHighlightFiber';
import SpliceIcon from 'esm-web-pro/components/layout/sidePanel/icons/SpliceIcon';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import SpliceContent from './SpliceContent';
import Point14Actions from '../point14/Point14Actions';
import Point14Attributes from '../point14/Point14Attributes';

type Props = {
    panelId: String,
    object: Object,
    map: { is_draft: boolean },
    resource: Object,
    match: Match,
    updateObject: Function,
    leaflet: LeafletContext,
    editor: BaseEditor,
    intl: IntlShape,
}

const Splice = (props: Props) => {
    const [fibersFocusCallbacks, setFibersFocusCallbacks] = React.useState([]);
    const [clickedRow, setClickedRow] = React.useState(null);
    const {
        match, object: splice, map: networkMap, updateObject, resource, leaflet: { map }, editor,
        intl: { formatMessage }, panelId,
    } = props;
    const {
        fiber_starts: fiberStarts, fiber_ends: fiberEnds, path, coordinates,
    } = splice;

    const pointCoords = [...coordinates.coordinates].reverse();
    const focusPoint = UseHighlightPoint(pointCoords, splice.sequence);
    const [highlightFunction, highlightCleanUp] = useHighlightFiber();

    const fibers = [...fiberStarts, ...fiberEnds];


    React.useEffect(() => {
        editor.unregisterAllLoadingCallback();
        editor.registerLoadingCallback(focusPoint);
        const circuitPath = [];
        const fibersIds = fibers.map((f) => Number(f.id));
        Object.keys(path).forEach((key) => {
            let fiberPath = path[key].map((c) => [c[1], c[0]]);

            if (!isEqual(pointCoords, fiberPath[0])) {
                fiberPath = fiberPath.reverse();
            }
            const fiberCallback = highlightFunction(fiberPath, fibersIds.includes(Number(key)));

            fibersFocusCallbacks.push(fiberCallback);
            circuitPath.push(...fiberPath);
        });
        fibers.forEach((fiber) => editor
            .registerMultipleLoadingCallbacks(numberPointsCallbacksFiber(fiber)));
        fibersFocusCallbacks.forEach((callback) => editor.registerLoadingCallback(callback));
        setFibersFocusCallbacks(fibersFocusCallbacks);
        if (map) {
            map.highlightedGeometry = {
                bounds: circuitPath, center: pointCoords,
            };
            map.flyToBoundsCenter(circuitPath, pointCoords, false);
        }
        editor.reloadFeatures();
        return () => {
            editor.unregisterAllLoadingCallback();
            highlightCleanUp();
        };
    }, []);

    const handleView = (clickedKey = null, callbacks = null, bounds = null) => {
        editor.unregisterAllLoadingCallback();
        if (callbacks) {
            callbacks.forEach((callback) => editor.registerLoadingCallback(callback));
        } else {
            fibersFocusCallbacks.forEach((callback) => editor.registerLoadingCallback(callback));
            editor.registerLoadingCallback(focusPoint);
            fibers.forEach((fiber) => editor
                .registerMultipleLoadingCallbacks(numberPointsCallbacksFiber(fiber)));
        }
        if (map) {
            if (bounds) {
                map.highlightedGeometry = {
                    bounds, center: pointCoords,
                };
                map.flyToBoundsCenter(bounds, pointCoords, false);
            } else {
                map.highlightedGeometry = {
                    bounds: null, center: pointCoords,
                };
                map.panTo(pointCoords);
            }
        }
        setClickedRow(clickedKey);
        editor.reloadFeatures();
    };

    const handleEdit = (data: Object) => {
        updateObject(data);
    };

    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            title={splice.sequence}
            titleLabel={formatMessage(point14Messages.splice)}
            titleIcon={<SpliceIcon />}
            titleOnView={handleView}
            actions={<NavigateBackButton panelId={panelId} />}
        >
            <Box height="100%" justify="between">
                <Box flex>
                    <UpperContainer>
                        <HorizontalLine color="grey" />
                        <Point14Attributes point14={splice} url={match.url} onView={handleView} />
                        <HorizontalLine color="grey" />
                        <Box direction="row">
                            <Point14Actions
                                point14={splice}
                                map={networkMap}
                                updateObject={updateObject}
                                resource={resource}
                                onEdit={handleEdit}
                                onView={handleView}
                                circuitPath={path}
                                editor={editor}
                            />
                        </Box>
                        <HorizontalLine color="grey" />
                    </UpperContainer>
                    <BottomContainer background="white">
                        <SpliceContent
                            pointCoords={pointCoords}
                            spliceContainer={splice.container}
                            fibers={fibers}
                            onView={handleView}
                            circuitPath={path}
                            clicked={clickedRow}
                        />
                    </BottomContainer>
                </Box>
            </Box>
        </SidePane>
    );
};

export default withLeaflet(injectIntl(withRouter(Splice)));
